// Rotas da área autenticada: labels, ícones, permissões (como em App.js) e palavras-chave para o comando rápido (Ctrl/⌘+K).
import {
  LayoutDashboard,
  CalendarDays,
  MessageCircle,
  UploadCloud,
  Church,
  ClipboardList,
  FileBarChart2,
  FileText,
  Users,
  Building2,
  UserCog,
  Globe2,
  ExternalLink,
  Network,
  Palette,
  ShieldCheck,
  History,
  KeyRound,
} from "lucide-react";
import { can } from "../utils/permissions";

/** null = qualquer utilizador autenticado */
const ANY = null;

/**
 * @typedef {Object} AppNavItemDef
 * @property {string} to
 * @property {string} label
 * @property {*} Icon — componente lucide-react
 * @property {string | null} perm — null: todos; senão a permissão necessária (utils/permissions)
 * @property {boolean} [end]
 * @property {string[]} [keywords]
 * @property {boolean} [mainNav] — false: só no comando rápido (Ctrl/⌘+K), não na barra
 * @property {boolean} [superAdminOnly] — só para o administrador da plataforma
 */

/** @type {AppNavItemDef[]} */
export const APP_NAV_ITEM_DEFS = [
  {
    to: "/dashboard",
    label: "Dashboard",
    end: true,
    Icon: LayoutDashboard,
    perm: ANY,
    keywords: ["início", "inicio", "painel", "resumo", "estatísticas", "estatisticas", "métricas", "metricas"],
  },
  {
    to: "/plano-anual",
    label: "Plano Anual",
    Icon: CalendarDays,
    perm: "plan.view",
    keywords: ["calendário", "calendario", "ano", "programação", "programacao", "eventos"],
  },
  {
    to: "/whatsapp",
    label: "WhatsApp (Programa)",
    Icon: MessageCircle,
    perm: "services.view",
    keywords: ["partilha", "partilhar", "mensagem", "semana", "programa"],
  },
  {
    label: "Ofertas",
    to: "/importar-ofertas",
    Icon: UploadCloud,
    perm: "offerings.edit",
    keywords: ["excel", "ofertas", "importação", "importacao", "financeiro", "tesouraria", "ficheiro", "editar", "corrigir"],
  },
  {
    to: "/services",
    label: "Cultos",
    Icon: Church,
    perm: "services.view",
    keywords: ["serviços", "servicos", "domingo", "reuniões", "reunioes", "culto"],
  },
  {
    to: "/schedules",
    label: "Escala",
    Icon: ClipboardList,
    perm: "services.view",
    keywords: ["escalas", "participantes", "funções", "funcoes", "pregador", "equipa", "equipe"],
  },
  {
    to: "/reports",
    label: "Relatório Financeiro",
    Icon: FileBarChart2,
    perm: "finance.view",
    keywords: ["relatórios", "relatorios", "finanças", "financas", "ofertas", "totais", "mês", "mes"],
  },
  {
    to: "/relatorio-do-culto",
    label: "Relatório do Culto",
    Icon: FileText,
    perm: "reports.edit",
    keywords: ["relatório culto", "relatorio culto", "presença", "presenca", "coleta", "secretaria", "pastoral"],
  },
  {
    to: "/membros",
    label: "Membros",
    Icon: Users,
    perm: "members.view",
    keywords: ["cadastro", "cadastro membros", "lista", "fieis", "fiéis"],
  },
  {
    to: "/departamentos",
    label: "Departamentos",
    Icon: Building2,
    perm: "members.view",
    keywords: ["ministérios", "ministerios", "secções", "seccoes", "equipas"],
  },
  {
    to: "/seguranca",
    label: "Segurança da conta",
    Icon: ShieldCheck,
    perm: ANY,
    keywords: ["2fa", "dois passos", "autenticação", "autenticacao", "segurança", "seguranca", "código", "codigo", "telemóvel"],
  },
  {
    to: "/dados-da-igreja",
    label: "Dados da igreja",
    Icon: Palette,
    perm: "church.edit",
    keywords: ["igreja", "logótipo", "logotipo", "cor", "marca", "identidade", "nome", "morada", "contacto"],
  },
  {
    to: "/utilizadores",
    label: "Utilizadores",
    Icon: UserCog,
    perm: "users.manage",
    keywords: ["usuários", "usuarios", "contas", "administração", "administracao", "acesso", "permissões", "permissoes"],
  },
  {
    to: "/perfis",
    label: "Perfis e permissões",
    Icon: KeyRound,
    perm: "users.manage",
    keywords: ["perfis", "papéis", "papeis", "permissões", "permissoes", "acesso", "funções", "funcoes", "roles"],
  },
  {
    to: "/registo-de-atividade",
    label: "Registo de atividade",
    Icon: History,
    perm: "activity.view",
    keywords: ["histórico", "historico", "auditoria", "log", "quem fez", "alterações", "alteracoes", "segurança", "seguranca"],
  },
  {
    to: "/public-admin",
    label: "Público (Admin)",
    Icon: Globe2,
    perm: "site.edit",
    keywords: ["site", "homepage", "conteúdo público", "conteudo publico", "página pública", "pagina publica", "edição pública", "edicao publica"],
  },
  {
    to: "/plataforma",
    label: "Plataforma",
    Icon: Network,
    perm: ANY,
    superAdminOnly: true,
    keywords: ["igrejas", "plataforma", "nova igreja", "trocar igreja", "alternar", "super"],
  },
  {
    to: "/public",
    label: "Site público (pré-visualizar)",
    Icon: ExternalLink,
    perm: ANY,
    mainNav: false,
    keywords: ["visitante", "externo", "igreja online", "homepage", "landing", "pré-visualizar", "pre-visualizar", "preview"],
  },
];

export function stripAccents(s) {
  return String(s)
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase();
}

/**
 * @param {object | null | undefined} user — utilizador da sessão
 * @returns {Array<AppNavItemDef & { keywords: string[] }>}
 */
export function getAppNavItemsForUser(user) {
  return APP_NAV_ITEM_DEFS.filter((def) => {
    if (def.superAdminOnly && !user?.isSuperAdmin) return false;
    if (def.perm == null) return true;
    return can(user, def.perm);
  }).map((def) => ({
    ...def,
    keywords: def.keywords || [],
  }));
}

/** Itens da barra superior (exclui entradas só para comando rápido). */
export function getMainNavItemsForUser(user) {
  return getAppNavItemsForUser(user).filter((def) => def.mainNav !== false);
}

/**
 * @param {AppNavItemDef & { keywords?: string[] }} item
 * @param {string} query
 */
export function navItemMatchesQuery(item, query) {
  const q = stripQuery(query);
  if (!q) return true;
  const parts = [item.label, ...(item.keywords || []), item.to.replace(/^\//, "")];
  const haystack = stripAccents(parts.join(" "));
  return haystack.includes(q);
}

function stripQuery(q) {
  return stripAccents(String(q || "").trim());
}
